/**
 * Live + shared audit records for the admin Audit log tab.
 *
 * Shared history comes from GET `/admin/audit-events`. A 404/501 is the
 * expected state until that endpoint ships — the tab still renders this
 * browser's ring buffer so the surface is usable the moment it's deployed.
 */

import { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { getAdminAuditEvents, isAdminAuditUnavailable } from '@/lib/api/api'
import type { AdminAuditRecord } from '@/lib/api/admin-types'
import { reportAdminFailure, type AdminAuthor } from '@/lib/observability/admin'
import {
  isAuditRecord,
  mergeAuditRecords,
  readLocalAdminAudit,
  subscribeAdminAudit,
} from '@/lib/observability/adminAudit'
import { resolveAdminFunnelWindow, type AdminFunnelWindow } from '@/lib/utils/adminPeriod'

type UseAdminAuditLogArgs = {
  token: string | null
  enabled: boolean
  period: string
  author: AdminAuthor
}

type SharedAuditPayload = {
  events: AdminAuditRecord[]
  available: boolean
}

type UseAdminAuditLogReturn = {
  events: AdminAuditRecord[]
  isLoading: boolean
  isFetching: boolean
  sharedAvailable: boolean
  range: AdminFunnelWindow
  refetch: () => void
}

const ISO = 'yyyy-MM-dd'

export function useAdminAuditLog({
  token,
  enabled,
  period,
  author,
}: UseAdminAuditLogArgs): UseAdminAuditLogReturn {
  const range = resolveAdminFunnelWindow(period)
  const [localEvents, setLocalEvents] = useState<AdminAuditRecord[]>(() => readLocalAdminAudit())

  useEffect(() => subscribeAdminAudit(setLocalEvents), [])

  const startDate = format(range.from, ISO)
  const endDate = format(range.to, ISO)

  const query = useQuery<SharedAuditPayload>({
    queryKey: ['admin-audit-events', startDate, endDate],
    queryFn: async () => {
      try {
        const res = await getAdminAuditEvents(token as string, {
          startDate,
          endDate,
          limit: 500,
        })
        return {
          events: (res.events ?? []).filter(isAuditRecord).map((event) => ({
            ...event,
            source: 'shared' as const,
          })),
          available: true,
        }
      } catch (error) {
        // Endpoint not shipped yet — local buffer is the trail until it is.
        if (isAdminAuditUnavailable(error)) return { events: [], available: false }
        throw error
      }
    },
    enabled: enabled && !!token,
    retry: false,
    staleTime: 30_000,
  })

  useEffect(() => {
    if (!query.error) return
    reportAdminFailure(
      'load_audit_log',
      query.error,
      author,
      { period },
      `load_audit_log:${period}`,
    )
  }, [query.error, author, period])

  const events = useMemo(
    () => mergeAuditRecords(query.data?.events ?? [], localEvents),
    [query.data?.events, localEvents],
  )

  return {
    events,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    sharedAvailable: query.data?.available === true,
    range,
    refetch: query.refetch,
  }
}
